import { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { buildQrOptions, ERROR_CORRECTION, type QrDesign } from '../../../lib/qr-design';
import styles from './DesignedQr.module.css';

/**
 * A designed QR code (`lib/qr-design.ts`) with its address printed beneath it — ALWAYS printed: a reader
 * who would rather type can, a screen reader has something to read, and a sticker pasted over a printed
 * code is visible as a mismatch (design §5.1, §12.2).
 *
 * `qr-code-styling` is imported inside the effect, never at module scope: it is a browser library (its SVG
 * path needs a `document`). So the plate starts with a PLAIN code that `qrcode.react` renders on the server
 * too — same payload, same ink, same error correction — and the designed code replaces it once drawn. Before
 * that base layer the server painted an empty plate, so the first paint, a slow phone and every reader
 * without JavaScript saw a blank "Scan with your phone" box.
 */
export interface DesignedQrProps {
  design: QrDesign;
  /**
   * What is printed under the code — the link without its scheme (`profileDisplayUrl`). Empty ONLY for a
   * decorative thumbnail inside an `aria-hidden` wrapper (the owner list), whose row prints the address.
   */
  caption: string;
  /** The accessible name of the code itself. */
  label: string;
  /** The rendered edge in px. The SVG is drawn at a fixed resolution and scaled to this. */
  size?: number;
  className?: string;
}

const DRAW_SIZE = 512;

export function DesignedQr({ design, caption, label, size = 180, className }: DesignedQrProps) {
  const host = useRef<HTMLDivElement>(null);
  // The design is compared by value: callers build it inline, and a new object per render must not
  // tear the code down and redraw it.
  const key = JSON.stringify(design);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let live = true;
    void import('qr-code-styling')
      .then(({ default: QRCodeStyling }) => {
        if (!live) return;
        el.replaceChildren();
        const qr = new QRCodeStyling(buildQrOptions({ design: JSON.parse(key) as QrDesign, size: DRAW_SIZE }));
        qr.append(el);
        const svg = el.querySelector('svg');
        if (svg) {
          // Drawn at DRAW_SIZE and scaled by CSS, so one render serves every size it is shown at.
          if (!svg.getAttribute('viewBox')) svg.setAttribute('viewBox', `0 0 ${DRAW_SIZE} ${DRAW_SIZE}`);
          svg.setAttribute('width', '100%');
          svg.setAttribute('height', '100%');
          svg.setAttribute('aria-hidden', 'true');
          setDrawn(true);
        }
      })
      .catch(() => {
        // No code is drawn; the printed address below still carries the link.
      });
    return () => {
      live = false;
      setDrawn(false);
    };
  }, [key]);

  return (
    <figure className={[styles.root, className].filter(Boolean).join(' ')}>
      <div className={styles.plate} style={{ width: size, height: size }} role="img" aria-label={label}>
        {drawn ? null : (
          <QRCodeSVG
            value={design.payload.value}
            level={design.logo.kind === 'none' ? ERROR_CORRECTION.DEFAULT : ERROR_CORRECTION.WITH_LOGO}
            fgColor={design.body.fill.color}
            bgColor={design.background.color}
            marginSize={2}
            className={styles.fallback}
            aria-hidden="true"
            data-testid="qr-fallback"
          />
        )}
        {/* React never renders into the host: the library owns its children. */}
        <div ref={host} className={styles.host} />
      </div>
      {caption ? (
        <figcaption className={styles.caption} dir="ltr">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}
